import { AlertTriangle, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CheckCircleIcon from '@/assets/icons/check-circle.svg';
import XCircleIcon from '@/assets/icons/x-circle.svg';
import { Separator } from '@/components/ui/separator';
import { TruncatedText } from '@/components/ui/truncated-text';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/utils/classnames';

import { ChannelStatusRow } from './channel-status-row';

import type { OpeningResult } from '../types';

interface OpeningResultCardProps {
  opening: OpeningResult;
}

export function OpeningResultCard({ opening }: OpeningResultCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const intl = useIntl();

  const failedCount = opening.channels.filter(
    c => c.status === 'failed',
  ).length;

  const allPosted = failedCount === 0;
  const allFailed = failedCount === opening.channels.length;

  return (
    <li
      className={cn(
        'bg-fx-neutral-50 border-fx-neutral-100 flex flex-col gap-3 rounded-lg border',
        isExpanded && 'pb-3',
      )}
    >
      <button
        type="button"
        className={cn(
          'hover:bg-fx-neutral-100 flex items-center justify-between gap-2 rounded-lg p-3',
          isExpanded && '-mb-3 rounded-b-none',
        )}
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <TruncatedText variant="small1" as="span">
          {opening.openingTitle}
        </TruncatedText>

        <div className="flex shrink-0 items-center gap-2">
          {allPosted && (
            <div className="flex items-center gap-1">
              <img src={CheckCircleIcon} alt="" />
              <Typography variant="small1" className="text-fx-green">
                <FormattedMessage
                  defaultMessage="All posted"
                  description="Badge shown when all channels for an opening posted successfully"
                />
              </Typography>
            </div>
          )}

          {allFailed && (
            <div className="flex items-center gap-1">
              <img src={XCircleIcon} alt="" />
              <Typography variant="small1" className="text-fx-rose">
                <FormattedMessage
                  defaultMessage="All failed"
                  description="Badge shown when all channels for an opening failed to post"
                />
              </Typography>
            </div>
          )}

          {!allPosted && !allFailed && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="text-fx-rose size-3" />
              <Typography variant="small1" className="text-fx-rose">
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'Failed for {count} {count, plural, one {channel} other {channels}}',
                    description:
                      'Badge shown when some channels failed for an opening',
                  },
                  { count: failedCount },
                )}
              </Typography>
            </div>
          )}

          <ChevronDown
            className={cn(
              'size-4 transition-transform',
              isExpanded && 'rotate-180',
            )}
          />
        </div>
      </button>

      {isExpanded && (
        <>
          <Separator />
          <ul className="flex flex-col gap-3">
            {opening.channels.map(channel => (
              <ChannelStatusRow
                key={`${opening.openingId}-${channel.channelSlug}`}
                channel={channel}
              />
            ))}
          </ul>
        </>
      )}
    </li>
  );
}
